import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Modal,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Grid,
  Chip,
  Divider,
  InputAdornment,
  styled,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DevicesIcon from "@mui/icons-material/Devices";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import AppleIcon from "@mui/icons-material/Apple";
import SaveIcon from "@mui/icons-material/Save";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import useGetAvailableDevices from "../../useQuery/useGetAvailableDevicesQuery";
import { useDeleteCapability } from "../../useQuery";
import { useNotification } from "../../hooks";

// Styled Components
const StyledButton = styled(Button)`
  padding: 12px 24px;
  border-radius: 8px;
  text-transform: capitalize;
  font-weight: 500;
`;

const StyledListItem = styled(ListItem)(({ theme }) => ({
  backgroundColor: "#f8f9fc",
  marginBottom: theme.spacing(1),
  borderRadius: "8px",
  padding: theme.spacing(2),
  border: "1px solid rgba(0, 0, 0, 0.03)",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#eef1f8",
    borderColor: "rgba(37, 80, 209, 0.1)",
  },
}));

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 0px;
    border: 1px solid #e0e0e0;
    background-color: #ffffff;
    &:focus-within {
      border-color: #2550d1;
      box-shadow: 0 0 0 2px rgba(37, 80, 209, 0.1);
    }
  }
`;

const FormField = ({ label, tooltip, children }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      <Typography color="#666" fontSize="14px" fontWeight={500}>
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} arrow>
          <IconButton size="small" sx={{ ml: 0.5, color: "text.secondary" }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
    {children}
  </Box>
);

// iOS simulator lines look like: "iPhone 16 (7906CAF5-0085-4BA0-B2E6-4AF46581B052) (Booted)"
// Appium's XCUITest driver needs ONLY the UUID for `udid`, not the full descriptive line.
// This regex pulls the UUID out regardless of surrounding text/state.
const IOS_UDID_REGEX =
  /([0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}|[0-9A-Fa-f]{8}(?:-[0-9A-Fa-f]{4}){2}[0-9A-Fa-f]{16})/;

const extractIosUdid = (rawDeviceString) => {
  if (!rawDeviceString) return "";
  const match = rawDeviceString.match(IOS_UDID_REGEX);
  return match ? match[1] : rawDeviceString.trim();
};

const CapabilitiesManager = ({
  savedCapabilities = [],
  onSaveCapability,
  isSaving = false,
  onRefetchCapabilities,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingCapabilityId, setDeletingCapabilityId] = useState(null);
  const [editingCapability, setEditingCapability] = useState(null);
  const [capabilityName, setCapabilityName] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("");
  // Keeps the full descriptive device string for iOS (e.g. "iPhone 16 (UUID) (Booted)")
  // so the dropdown can still display something human-readable even though
  // capabilities.deviceName only ever stores the clean UDID.
  const [selectedDeviceLabel, setSelectedDeviceLabel] = useState("");

  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    capability: null,
  });

  const [hasFormChanged, setHasFormChanged] = useState(false);

  const [capabilities, setCapabilities] = useState({
    platformName: "",
    platformVersion: "",
    deviceName: "",
    automationName: "",
    appiumUrl: "",
    systemId: "",
    mobileNo: "",
    systemType: "",
  });

  const notify = useNotification();
  const platformForQuery = selectedPlatform ? selectedPlatform.toLowerCase() : "";
  const {
    data,
    isLoading: devicesLoading,
    error: devicesError,
    refetch: refetchDevices,
    status,
  } = useGetAvailableDevices(platformForQuery, {
    enabled: isModalOpen && !!selectedPlatform,
  });

  const availableDevices = Array.isArray(data)
    ? data
    : Array.isArray(data?.devices)
      ? data.devices
      : [];

  useEffect(() => {
    if (status === "success" && data?.message && availableDevices.length === 0) {
      notify(data.message, "warning");
    }
  }, [status, data]);

  const deleteCapabilityMutation = useDeleteCapability({
    onSuccess: (data) => {
      notify("Capability deleted successfully!", "success");
      onRefetchCapabilities?.();
      setDeletingCapabilityId(null);
    },
    onError: (error) => {
      notify("Failed to delete capability", "error");
      setDeletingCapabilityId(null);
    },
  });

  useEffect(() => {
    if (selectedPlatform === "Android") {
      setCapabilities((prev) => ({
        ...prev,
        automationName: "UiAutomator2",
        platformName: "Android",
      }));
    } else if (selectedPlatform === "iOS") {
      setCapabilities((prev) => ({
        ...prev,
        automationName: "XCUITest",
        platformName: "iOS",
      }));
    }
  }, [selectedPlatform]);

  useEffect(() => {
    if (editingCapability) {
      const capData = editingCapability.capability || {};

      const changed =
        capabilityName !== (editingCapability.name || "") ||
        capabilities.platformVersion !== (capData.platformVersion || "") ||
        capabilities.deviceName !== (capData.deviceName || "") ||
        capabilities.appiumUrl !== (capData.appiumUrl || "") ||
        capabilities.systemId !== (capData.systemId || "") ||
        capabilities.mobileNo !== (capData.mobileNo || "") ||
        capabilities.systemType !== (capData.systemType || "") ||
        selectedPlatform !== (capData.platformName || "");

      setHasFormChanged(changed);
    } else {
      setHasFormChanged(true);
    }
  }, [capabilityName, capabilities, selectedPlatform, editingCapability]);

  const handleCapabilityChange = (key, value) => {
    setCapabilities((prev) => ({ ...prev, [key]: value }));
  };

  // Called from the Device Name <Select>. For iOS, the dropdown option is the
  // full raw simctl line — we display that, but only persist the parsed UDID.
  const handleDeviceSelect = (rawValue) => {
    if (selectedPlatform === "iOS") {
      setSelectedDeviceLabel(rawValue);
      handleCapabilityChange("deviceName", extractIosUdid(rawValue));
    } else {
      setSelectedDeviceLabel(rawValue);
      handleCapabilityChange("deviceName", rawValue);
    }
  };

  const handleSave = async () => {
    if (!capabilityName.trim()) {
      notify("Please enter a name for the capability", "error");
      return;
    }
    if (!selectedPlatform) {
      notify("Please select a platform", "error");
      return;
    }
    if (!capabilities.platformVersion.trim()) {
      notify("Please enter a platform version", "error");
      return;
    }
    if (!capabilities.deviceName.trim()) {
      notify("Please select a device", "error");
      return;
    }
    if (selectedPlatform === "iOS" && !IOS_UDID_REGEX.test(capabilities.deviceName)) {
      notify(
        "Device UDID doesn't look valid. Please select a device from the dropdown rather than typing it manually.",
        "error",
      );
      return;
    }
    if (!capabilities.appiumUrl.trim()) {
      notify("Please enter the Appium URL", "error");
      return;
    }
    if (!capabilities.systemId.trim()) {
      notify("Please enter the System Id", "error");
      return;
    }
    if (!capabilities.mobileNo.trim()) {
      notify("Please enter the Mobile No", "error");
      return;
    }
    if (!capabilities.systemType.trim()) {
      notify("Please select the Mobile Type", "error");
      return;
    }

    try {
      const payload = {
        name: capabilityName,
        capabilities: capabilities,
      };

      if (editingCapability) {
        payload.capId = editingCapability.CapId;
      }

      await onSaveCapability?.(payload);

      notify(
        editingCapability ? "Capability updated successfully!" : "Capability saved successfully!",
        "success",
      );

      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      notify(
        `Failed to ${editingCapability ? "update" : "save"} capability: ` +
          (error.message || "Unknown error"),
        "error",
      );
    }
  };

  const handleDeleteCapability = async (capability) => {
    if (!capability.CapId) {
      notify("Invalid capability ID", "error");
      return;
    }

    setDeleteConfirmation({
      open: true,
      capability: capability,
    });
  };

  const handleConfirmDelete = async () => {
    const capability = deleteConfirmation.capability;

    try {
      setDeletingCapabilityId(capability.CapId);
      await deleteCapabilityMutation.mutateAsync(capability.CapId);
      setDeleteConfirmation({ open: false, capability: null });
    } catch (error) {
      console.error("Delete capability error:", error);
    }
  };

  const resetForm = () => {
    setCapabilities({
      platformName: "",
      platformVersion: "",
      deviceName: "",
      automationName: "",
      appiumUrl: "",
      systemId: "",
      mobileNo: "",
      systemType: "",
    });
    setSelectedPlatform("");
    setCapabilityName("");
    setSelectedDeviceLabel("");
    setEditingCapability(null);
    setHasFormChanged(false);
  };

  const handleEditCapability = (capability) => {
    const capData = capability.capability || {};

    setCapabilityName(capability.name || "");
    setSelectedPlatform(capData.platformName || "");
    setCapabilities({
      platformName: capData.platformName || "",
      platformVersion: capData.platformVersion || "",
      deviceName: capData.deviceName || "",
      automationName: capData.automationName || "",
      appiumUrl: capData.appiumUrl || "",
      systemId: capData.systemId || "",
      mobileNo: capData.mobileNo || "",
      systemType: capData.systemType || "",
    });
    // We only ever stored the clean UDID, so the dropdown will show that
    // until a fresh device list loads and the user can re-pick the full label.
    setSelectedDeviceLabel(capData.deviceName || "");

    setEditingCapability(capability);
    setIsModalOpen(true);
  };

  const renderPlatformSelection = () => (
    <FormField label="Select Platform*">
      <Grid container spacing={2}>
        {["Android", "iOS"].map((platform) => (
          <Grid item xs={12} sm={6} key={platform}>
            <Button
              fullWidth
              variant={selectedPlatform === platform ? "contained" : "outlined"}
              onClick={() => setSelectedPlatform(platform)}
              sx={{
                padding: 1.5,
                borderRadius: "8px",
                border: `1px solid ${selectedPlatform === platform ? "#2550d1" : "#e0e0e0"}`,
                backgroundColor: selectedPlatform === platform ? "#2550d1" : "#ffffff",
                color: selectedPlatform === platform ? "#ffffff" : "#333",
                height: "70px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: selectedPlatform === platform ? "#1e3fa1" : "#f5f5f5",
                },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                {platform === "Android" ? (
                  <PhoneAndroidIcon sx={{ fontSize: 24, mb: 0.5 }} />
                ) : (
                  <AppleIcon sx={{ fontSize: 24, mb: 0.5 }} />
                )}
                <Typography variant="subtitle2" fontWeight={500}>
                  {platform}
                </Typography>
              </Box>
            </Button>
          </Grid>
        ))}
      </Grid>
    </FormField>
  );

  const renderFormFields = () => (
    <Box>
      {renderPlatformSelection()}
      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormField label="Configuration Name*">
            <StyledTextField
              fullWidth
              value={capabilityName}
              onChange={(e) => setCapabilityName(e.target.value)}
              placeholder="Enter configuration name"
              variant="outlined"
              size="small"
            />
          </FormField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormField label="Platform Version*">
            <StyledTextField
              fullWidth
              value={capabilities.platformVersion}
              onChange={(e) => handleCapabilityChange("platformVersion", e.target.value)}
              placeholder={selectedPlatform === "Android" ? "E.g. 13.0" : "E.g. 16.2"}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DevicesIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </FormField>
        </Grid>
      </Grid>

      <FormField
        label="Device Name*"
        tooltip={
          selectedPlatform === "iOS"
            ? "Only the device UDID is stored — the simulator name/state shown here is for display only"
            : undefined
        }
      >
        <FormControl fullWidth size="small">
          <InputLabel>Select Device</InputLabel>
          <Select
            value={selectedPlatform === "iOS" ? selectedDeviceLabel : capabilities.deviceName}
            onChange={(e) => handleDeviceSelect(e.target.value)}
            label="Select Device"
            disabled={!selectedPlatform || devicesLoading}
            startAdornment={
              <InputAdornment position="start">
                {selectedPlatform === "Android" ? (
                  <PhoneAndroidIcon fontSize="small" />
                ) : (
                  <AppleIcon fontSize="small" />
                )}
              </InputAdornment>
            }
            sx={{
              borderRadius: "0px",
              backgroundColor: "#ffffff",
              "& .MuiOutlinedInput-root": {
                borderRadius: "0px",
                "&:focus-within": {
                  borderColor: "#2550d1",
                  boxShadow: "0 0 0 2px rgba(37, 80, 209, 0.1)",
                },
              },
            }}
          >
            {devicesLoading && (
              <MenuItem disabled>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Loading devices...
                </Box>
              </MenuItem>
            )}
            {devicesError && (
              <MenuItem disabled>
                <Typography color="error" fontSize="14px">
                  Error loading devices
                </Typography>
              </MenuItem>
            )}
            {!devicesLoading && !devicesError && availableDevices.length === 0 && (
              <MenuItem disabled>
                <Typography color="text.secondary" fontSize="14px">
                  No devices available
                </Typography>
              </MenuItem>
            )}
            {!devicesLoading &&
              !devicesError &&
              availableDevices.length > 0 &&
              availableDevices.map((device, index) => (
                <MenuItem key={index} value={device}>
                  <Typography>{device}</Typography>
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {selectedPlatform === "iOS" && capabilities.deviceName && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            Stored UDID: {capabilities.deviceName}
          </Typography>
        )}
        {devicesError && (
          <Box sx={{ mt: 1 }}>
            <Alert
              severity="warning"
              sx={{ fontSize: "12px", py: 0.5 }}
              action={
                <Button size="small" onClick={() => refetchDevices()} sx={{ fontSize: "11px" }}>
                  Retry
                </Button>
              }
            >
              Failed to load devices. You can still enter manually or retry.
            </Alert>
          </Box>
        )}
        {!selectedPlatform && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            Please select a platform first to see available devices
          </Typography>
        )}
      </FormField>

      <FormField label="Appium URL*" tooltip="The URL where Appium server is running">
        <StyledTextField
          fullWidth
          value={capabilities.appiumUrl}
          onChange={(e) => handleCapabilityChange("appiumUrl", e.target.value)}
          placeholder="http://127.0.0.1:4723/"
          variant="outlined"
          size="small"
        />
      </FormField>
      <FormField>
        <InputLabel>Device Type</InputLabel>
        <Select
          fullWidth
          value={capabilities.systemType}
          onChange={(e) => handleCapabilityChange("systemType", e.target.value)}
          label="Device Type"
        >
          <MenuItem value="Windows">Windows</MenuItem>
          <MenuItem value="Mac">Mac</MenuItem>
        </Select>
      </FormField>
      <FormField label="System Id" tooltip={`System ID`}>
        <StyledTextField
          fullWidth
          value={capabilities.systemId}
          onChange={(e) => handleCapabilityChange("systemId", e.target.value)}
          variant="outlined"
          size="small"
        />
      </FormField>
      <FormField label="Mobile No" tooltip={`Mobile No`}>
        <StyledTextField
          fullWidth
          value={capabilities.mobileNo}
          onChange={(e) => handleCapabilityChange("mobileNo", e.target.value)}
          variant="outlined"
          size="small"
        />
      </FormField>
      <FormField
        label="Automation Name"
        tooltip={`Auto-set based on platform: ${selectedPlatform === "Android" ? "UiAutomator2" : "XCUITest"}`}
      >
        <StyledTextField
          fullWidth
          value={capabilities.automationName}
          disabled
          variant="outlined"
          size="small"
          sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#f5f5f5" } }}
        />
      </FormField>
    </Box>
  );

  const renderSavedCapabilities = () => (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
          Saved Configurations
        </Typography>
        <StyledButton
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          startIcon={<AddIcon />}
          variant="contained"
          sx={{
            backgroundColor: "#2550d1",
            "&:hover": { backgroundColor: "#1e3fa1" },
          }}
        >
          Create New
        </StyledButton>
      </Box>
      {savedCapabilities?.length > 0 ? (
        <List sx={{ px: 0 }}>
          {savedCapabilities.map((capability, index) => {
            const capData = capability.capability || {};
            const isDeleting = deletingCapabilityId === capability.CapId;
            return (
              <StyledListItem key={index}>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight={600}>
                      {capability.name || `Configuration ${index + 1}`}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        size="small"
                        icon={
                          capData.platformName === "Android" ? (
                            <PhoneAndroidIcon fontSize="small" />
                          ) : (
                            <AppleIcon fontSize="small" />
                          )
                        }
                        label={capData.platformName || "Not specified"}
                        sx={{
                          mr: 1,
                          mb: 1,
                          backgroundColor:
                            capData.platformName === "Android"
                              ? "rgba(76, 175, 80, 0.1)"
                              : "rgba(33, 150, 243, 0.1)",
                          color: capData.platformName === "Android" ? "#388e3c" : "#1976d2",
                          borderRadius: "6px",
                        }}
                      />
                      <Chip
                        size="small"
                        label={`Device: ${capData.deviceName || "Not specified"}`}
                        sx={{ mr: 1, mb: 1, borderRadius: "6px" }}
                      />
                      <Chip
                        size="small"
                        label={`Version: ${capData.platformVersion || "Not specified"}`}
                        sx={{ mr: 1, mb: 1, borderRadius: "6px" }}
                      />
                      <Chip
                        size="small"
                        label={`AppiumUrl: ${capData.appiumUrl || "Not specified"}`}
                        sx={{ mr: 1, mb: 1, borderRadius: "6px" }}
                      />
                    </Box>
                  }
                />
                <StyledButton
                  variant="contained"
                  color="primary"
                  onClick={() => handleEditCapability(capability)}
                  sx={{
                    boxShadow: "0px 16px 30.6px 0px rgba(37, 80, 209, 0.27)",
                    mr: 2,
                  }}
                >
                  Edit
                </StyledButton>
                <StyledButton
                  variant="contained"
                  color="error"
                  onClick={() => handleDeleteCapability(capability)}
                  disabled={isDeleting}
                  sx={{
                    boxShadow: "0px 16px 30.6px 0px rgba(37, 80, 209, 0.27)",
                  }}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </StyledButton>
              </StyledListItem>
            );
          })}
        </List>
      ) : (
        <Box sx={{ textAlign: "center", py: 4, backgroundColor: "#f8f9fc", borderRadius: "8px" }}>
          <FolderOpenIcon sx={{ fontSize: 48, color: "rgba(0, 0, 0, 0.2)", mb: 2 }} />
          <Typography variant="subtitle1" sx={{ mb: 1, color: "text.secondary" }}>
            No saved configurations
          </Typography>
          <Typography variant="body2" sx={{ maxWidth: 400, color: "text.secondary" }}>
            Create and save device configurations to reuse them in future test sessions
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {renderSavedCapabilities()}
      <Modal
        open={isModalOpen}
        onClose={() => {
          resetForm();
          setIsModalOpen(false);
        }}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "12px",
            maxWidth: "600px",
            width: "100%",
            maxHeight: "80vh",
            overflow: "hidden",
            margin: "20px",
            boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box
            sx={{
              p: 2,
              backgroundColor: "#f8f9fc",
              borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center">
                <DevicesIcon sx={{ color: "#2550d1", mr: 1, fontSize: 20 }} />
                <Typography variant="h6" fontWeight={600}>
                  {editingCapability ? "Edit Device Capability" : "Create Device Capability"}
                </Typography>
              </Box>
              <IconButton
                onClick={() => {
                  resetForm();
                  setIsModalOpen(false);
                }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ p: 2, maxHeight: "calc(80vh - 120px)", overflow: "auto" }}>
            {renderFormFields()}
          </Box>

          <Box
            sx={{
              mb: 2,
              mr: 2,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              borderTop: "1px solid rgba(0, 0, 0, 0.06)",
              backgroundColor: "#f8f9fc",
            }}
          >
            <StyledButton
              onClick={() => {
                resetForm();
                setIsModalOpen(false);
              }}
              variant="outlined"
            >
              Cancel
            </StyledButton>
            <StyledButton
              onClick={handleSave}
              disabled={isSaving || (editingCapability && !hasFormChanged)}
              startIcon={<SaveIcon />}
              variant="contained"
              sx={{ backgroundColor: "#2550d1", "&:hover": { backgroundColor: "#1e3fa1" } }}
            >
              {isSaving
                ? editingCapability
                  ? "Updating..."
                  : "Saving..."
                : editingCapability
                  ? "Update Configuration"
                  : "Save Configuration"}
            </StyledButton>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, capability: null })}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "12px",
            maxWidth: "500px",
            width: "90%",
            p: 3,
            boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.2)",
          }}
        >
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Delete Device Capability
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Are you sure you want to delete capability "{deleteConfirmation.capability?.name}"? This
            action cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <StyledButton
              onClick={() => setDeleteConfirmation({ open: false, capability: null })}
              variant="outlined"
            >
              CANCEL
            </StyledButton>
            <StyledButton
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              disabled={deletingCapabilityId !== null}
              sx={{
                backgroundColor: "#d32f2f",
                "&:hover": { backgroundColor: "#c62828" },
              }}
            >
              {deletingCapabilityId !== null ? "DELETING..." : "DELETE"}
            </StyledButton>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default CapabilitiesManager;
